import { Routes, Route, Navigate } from "react-router-dom";
import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import InternalLayout from "layouts/internal";
import "assets/css/Plugins.css";
import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth"; // Add Firebase imports
import "@services/authMessageHandler"; // Auto-initialize auth message handler
import { TenantProvider } from "@contexts/TenantContext";
import { InternalAdminProvider } from "@contexts/InternalAdminContext";
import { isInternalPortalMode } from "@utils/portalHelper";
import AllTenanciesList from "views/internal/tenants";
import * as Sentry from "@sentry/react";

const App = () => {
  // Create a new context
  const [themeApp, setThemeApp] = useState<any>({
    "--background-100": "#F4F7FE",
    "--background-900": "#070f2e",
    "--shadow-100": "transparent",
    "--color-50": "#F0EFFF",
    "--color-100": "#E3E1FF",
    "--color-200": "#C9C5FF",
    "--color-300": "#AFA8FF",
    "--color-400": "#948BFF",
    "--color-500": "#5746EF",
    "--color-600": "#4635CC",
    "--color-700": "#3527A8",
    "--color-800": "#241A85",
    "--color-900": "#130E62",
  });
  const [mini, setMini] = useState(false);
  
  // Add authentication state
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Firebase auth listener
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // When the theme state changes, this effect will update the CSS variables
  useEffect(() => {
    let color;
    for (color in themeApp) {
      document.documentElement.style.setProperty(color, themeApp[color]);
    }
    //eslint-disable-next-line
  }, [themeApp]);

  // Protected route component
  const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
    if (loading) {
      return <div>Loading...</div>; // You can use a Horizon UI loader here
    }
    
    if (!user) {
      return <Navigate to="/auth/sign-in/default" replace />;
    }
    
    return children;
  };

  if (loading) {
    return <div>Loading...</div>; // Initial app loading state
  }

  // Check if we're on the internal admin domain
  const isInternalDomain = isInternalPortalMode();

  // Render internal admin app
  if (isInternalDomain) {
    return (
      <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
        <InternalAdminProvider>
          <Routes>
            <Route path="auth/*" element={<AuthLayout />} />
            <Route path="/" element={<InternalLayout />}>
              <Route index element={<AllTenanciesList />} />
              <Route path="tenants" element={<AllTenanciesList />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </InternalAdminProvider>
      </Sentry.ErrorBoundary>
    );
  }

  // Render main admin app
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <TenantProvider>
        <Routes>
          <Route path="auth/*" element={<AuthLayout />} />
          <Route
            path="admin/*"
            element={
              <ProtectedRoute>
                <AdminLayout
                  setMini={setMini}
                  mini={mini}
                  theme={themeApp}
                  setTheme={setThemeApp}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="dashboards/*"
            element={
              <ProtectedRoute>
                <AdminLayout
                  setMini={setMini}
                  mini={mini}
                  theme={themeApp}
                  setTheme={setThemeApp}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              user ?
              <Navigate to="/admin/intros/dashboard" replace /> :
              <Navigate to="/auth/sign-in/default" replace />
            }
          />
        </Routes>
      </TenantProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;